import React, { useEffect, useState } from "react"
import { IoClose } from "react-icons/io5"
import ShippingLocation from "./ShippingLocation"
import DeliveryDateSelector from "./DeliveryDateSelector"
import DeliverySlots from "./DeliverySlots"
import { ShippingData, ShippingInfo, DeliverySlot } from "./types/shipping"

interface ContentModalOrderScheduleProps {
  isOpen: boolean
  onClose: () => void
  shippingData: ShippingData
}

const ContentModalOrderSchedule: React.FC<ContentModalOrderScheduleProps> = ({ isOpen, onClose, shippingData }) => {
  const [selectedDateIndex, setSelectedDateIndex] = useState(0)
  const [selectedShippingType, setSelectedShippingType] = useState<"HD" | "SP">("HD")
  const [selectedStoreIndex, setSelectedStoreIndex] = useState(0)
  const [selectedDeliveryTypeIndex, setSelectedDeliveryTypeIndex] = useState(0)
  const [selectedSlot, setSelectedSlot] = useState<DeliverySlot | null>(null)

  // Reset state when modal opens or shipping data changes
  useEffect(() => {
    if (isOpen) {
      // Determine initial shipping type based on available data
      const initialShippingType = shippingData.shippingType.HD ? "HD" : "SP"
      setSelectedShippingType(initialShippingType)
      setSelectedDateIndex(0)
      setSelectedStoreIndex(0)
      setSelectedDeliveryTypeIndex(0)
      setSelectedSlot(null)
    }
  }, [isOpen, shippingData])

  if (!isOpen) return null

  const shippingOptions = shippingData.shippingType[selectedShippingType] || []

  // For SP, we use selectedStoreIndex to select the store, for HD we use selectedDeliveryTypeIndex for delivery type
  const currentOption = shippingOptions[selectedShippingType === "SP" ? selectedStoreIndex : selectedDeliveryTypeIndex]
  const schedules = currentOption?.schedules[0]
  const dates = schedules?.dates || []
  const selectedDate = dates[selectedDateIndex]

  const handleShippingTypeChange = (type: "HD" | "SP") => {
    setSelectedShippingType(type)
    setSelectedDateIndex(0)
    setSelectedStoreIndex(0)
    setSelectedDeliveryTypeIndex(0)
    setSelectedSlot(null)
  }

  const handleDateSelect = (index: number) => {
    setSelectedDateIndex(index)
    setSelectedSlot(null)
  }

  const handleSlotSelect = (slot: DeliverySlot) => {
    setSelectedSlot(slot)
  }

  const handleDeliveryTypeSelect = (index: number) => {
    setSelectedDeliveryTypeIndex(index)
    setSelectedDateIndex(0)
    setSelectedSlot(null)
  }

  const handleStoreSelect = (index: number) => {
    setSelectedStoreIndex(index)
    setSelectedDateIndex(0)
    setSelectedSlot(null)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-fadeIn">
      <div
        className="relative w-11/12 max-w-3xl max-h-[90vh] bg-white rounded-lg shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <h2 className="text-xl font-semibold text-gray-800">Información de Envío</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
            <IoClose size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="w-full p-6">
          {/* Shipping type selector */}
          {shippingData.shippingType.HD && shippingData.shippingType.SP && (
            <div className="mb-6">
              <div className="flex gap-4">
                <button
                  onClick={() => handleShippingTypeChange("HD")}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${
                    selectedShippingType === "HD"
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-200 hover:border-gray-300 text-gray-700"
                  }`}
                >
                  Entrega a Domicilio
                </button>
                <button
                  onClick={() => handleShippingTypeChange("SP")}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-colors ${
                    selectedShippingType === "SP"
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-200 hover:border-gray-300 text-gray-700"
                  }`}
                >
                  Retiro en Tienda
                </button>
              </div>
            </div>
          )}

          {/* Delivery type selector for HD */}
          {selectedShippingType === "HD" && shippingOptions.length > 1 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">Tipo de Entrega</h3>
              <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {shippingOptions.map((option: ShippingInfo, index: number) => (
                  <button
                    key={`${option?.schedules[0]?.deliveryType}-${index}`}
                    onClick={() => handleDeliveryTypeSelect(index)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                      selectedDeliveryTypeIndex === index
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <h4 className="font-medium text-gray-800">{option?.schedules[0]?.deliveryType}</h4>
                    <p className="text-sm text-gray-600">
                      {option?.schedules[0]?.sources[0]?.route[0]?.courier?.name || "Courier no especificado"}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Store selector for SP */}
          {selectedShippingType === "SP" && shippingOptions.length > 0 && (
            <div className="w-full mb-6">
              <h3 className="text-lg font-medium text-gray-800 mb-3">
                {shippingOptions.length > 1 ? "Tiendas de Retiro Disponibles" : "Tienda de Retiro"}
              </h3>
              <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {shippingOptions.map((option: ShippingInfo, index: number) => {
                  const sourceData = option.target.data as any
                  const _iterate_source = option.schedules[0]?.sources[0]?.data || {}
                  return (
                    <button
                      key={`${sourceData?.id}-${index}`}
                      onClick={() => handleStoreSelect(index)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                        selectedStoreIndex === index
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <h4 className="font-medium text-gray-800">{`Opción ${index + 1}`}</h4>
                      <p className="text-xs text-blue-600 mt-1">{`${_iterate_source?.id} - ${_iterate_source?.name}`}</p>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Location and shipping type */}
          {currentOption && schedules && (
            <>
              <ShippingLocation
                location={selectedShippingType === "SP" ? currentOption.target.data : currentOption.target.data}
                shippingType={currentOption.shippingType}
                deliveryType={schedules.deliveryType}
                currentSource={schedules?.sources[0]?.data}
              />

              {/* Delivery dates */}
              {dates.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-3">Fechas Disponibles</h3>
                  <DeliveryDateSelector
                    dates={dates}
                    selectedIndex={selectedDateIndex}
                    onSelectDate={handleDateSelect}
                  />
                </div>
              )}

              {/* Delivery slots */}
              {selectedDate && selectedDate.deliverySlots.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-lg font-medium text-gray-800 mb-3">Horarios Disponibles</h3>
                  <DeliverySlots
                    slots={selectedDate.deliverySlots.map((slot) => ({
                      from: slot.from,
                      to: slot.to,
                      label: slot.label,
                      deliveryType: schedules.deliveryType,
                    }))}
                    selectedSlot={selectedSlot}
                    onSlotSelect={handleSlotSelect}
                    deliveryType={schedules.deliveryType}
                  />
                </div>
              )}

              {/* Cost information */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Costo de envío:</span>
                  <span className="text-lg font-semibold text-green-600">
                    {schedules.cost === 0 ? "Gratis" : `S/ ${schedules.cost.toFixed(2)}`}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            className={`px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors`}
            onClick={onClose}
          >
            Aceptar
          </button>
        </div>
      </div>
    </div>
  )
}

export default ContentModalOrderSchedule
